/**
 * THE SIDE-EFFECT CLASSIFICATION of a tool (owner decision 20, Q5; gh #4418), in the halves
 * that need nothing but the tool itself: a platform native tool by its catalog entry, an HTTP
 * tool by its method, and a composed chain by its members. Two readers ask the whole of it:
 * - the backend's call path (`toolCallSideEffects` in `apps/backend/src/tools/side-effects.ts`),
 *   which adds what only the dispatcher knows — the resolved connector module — and holds a
 *   call whose effects are not `none` for spoken confirmation;
 * - the monitor rule contract (`isMonitorRuleCallableTool` in `contracts/setup/agents.ts`),
 *   which admits on a live call's `call_utterance` monitor only a tool classified `none`.
 * One classification, so a tool the call path would hold for confirmation is never one a rule
 * may run on a call unasked.
 *
 * A THIRD READER, of two of its halves: the replay axis (`tool-replay.ts`) takes an HTTP tool's
 * read rule from `httpToolSideEffects` (`none` reruns) and decodes a chain through
 * `composedChainMemberNames`. So a method this file reads as `none` is one a durable turn runs
 * again after a crash, not only one a call runs unconfirmed.
 *
 * UNKNOWN MEANS NOT NONE: an MCP tool's read-only hints come from its server, which the
 * platform does not vouch for, and a method or name this file does not recognise is unknown.
 */

import { NATIVE_TOOLS } from './native-tools';

export type ToolSideEffects = 'none' | 'side_effects' | 'unknown';

const READING_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);
const WRITING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const CATALOG_SIDE_EFFECTS: ReadonlyMap<string, boolean> = new Map(
  NATIVE_TOOLS.map((tool) => [tool.tool_name, tool.side_effects]),
);

/** An HTTP tool, by its method: GET, HEAD and OPTIONS read. No method is POST, as the executor sends it. */
export function httpToolSideEffects(method: unknown): ToolSideEffects {
  if (method === undefined || method === null || method === '') return 'side_effects';
  if (typeof method !== 'string') return 'unknown';
  const upper = method.trim().toUpperCase();
  if (READING_METHODS.has(upper)) return 'none';
  return WRITING_METHODS.has(upper) ? 'side_effects' : 'unknown';
}

/** A platform native tool, by the name it is dispatched under; a name the catalog lacks is unknown. */
export function catalogToolSideEffects(toolName: string): ToolSideEffects {
  const sideEffects = CATALOG_SIDE_EFFECTS.get(toolName);
  if (sideEffects === undefined) return 'unknown';
  return sideEffects ? 'side_effects' : 'none';
}

/** The more demanding of two: unknown over side effects over none. */
export function stricterSideEffects(a: ToolSideEffects, b: ToolSideEffects): ToolSideEffects {
  if (a === 'unknown' || b === 'unknown') return 'unknown';
  return a === 'side_effects' || b === 'side_effects' ? 'side_effects' : 'none';
}

/**
 * The tool names of a composed chain (`composed_tools`), in order: `undefined` for no chain, `null`
 * for one that cannot be read. The chain may be the decoded column or its JSON text, and a member
 * with no string `tool_name` is named `''`, which no catalog entry is. Both classification axes
 * read a chain through this (`withComposedChain`, `withComposedChainReplay`).
 */
export function composedChainMemberNames(composedTools: unknown): string[] | null | undefined {
  let chain: unknown = composedTools;
  if (typeof chain === 'string') {
    try {
      chain = JSON.parse(chain);
    } catch {
      return null;
    }
  }
  if (chain === undefined || chain === null) return undefined;
  if (!Array.isArray(chain)) return null;
  return chain.map((entry) => {
    const name = entry && typeof entry === 'object' ? (entry as { tool_name?: unknown }).tool_name : undefined;
    return typeof name === 'string' ? name : '';
  });
}

/**
 * A call raised to its composed chain (`composed_tools`): the chain's members run through the
 * platform native module by name, so each is classified from the catalog, and a member with
 * side effects gives the whole call side effects. An unreadable chain is unknown.
 */
export function withComposedChain(own: ToolSideEffects, composedTools: unknown): ToolSideEffects {
  const names = composedChainMemberNames(composedTools);
  if (names === undefined) return own;
  if (names === null) return 'unknown';
  return names.reduce<ToolSideEffects>((worst, name) => stricterSideEffects(worst, catalogToolSideEffects(name)), own);
}
